import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  viewChild,
  viewChildren,
  WritableSignal
} from '@angular/core';
import { ButtonSize, ButtonStyle } from '../../../../shared/enums/button.enum';
import { OptionSwitcherItem } from '../../../../shared/interfaces/option-switcher.interface';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { Dialog, DialogModule, DialogRef } from '@angular/cdk/dialog';
import { MealSelectionDialogComponent } from '../../../../shared/components/dialogs/meal-selection-dialog/meal-selection-dialog.component';
import { ConfirmDialogComponent } from '../../../../shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import {
  auditTime,
  distinctUntilChanged,
  filter,
  finalize,
  forkJoin,
  fromEvent,
  interval,
  map,
  pairwise,
  startWith,
} from 'rxjs';
import { SearchComponent } from '../../../../shared/components/controllers/search/search.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { OptionSwitcherComponent } from '../../../../shared/components/controllers/option-switcher/option-switcher.component';
import { WooCommerceProduct } from '../../../../core/interfaces/woo-commerce/api/products/product-api.interface';
import { SkeletonLoaderComponent } from '../../../../shared/components/loaders/skeleton-loader/skeleton-loader.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  WooCommerceRestaurantBanner,
  WooCommerceRestaurantHoursValue
} from '../../../../core/interfaces/woo-commerce/api/restaurant/restaurant-api.interface';
import { SkeletonDirective } from '../../../../shared/directives/skeleton-loading.directive';
import { RestaurantDetailsDialogComponent } from '../dialogs/restaurant-details-dialog/restaurant-details-dialog.component';
import { NgClass, UpperCasePipe } from '@angular/common';
import { ScheduleOrderDialogComponent } from '../../../../shared/components/dialogs/schedule-order-dialog/schedule-order-dialog.component';
import { RestaurantDeliveryMethodDialogComponent } from '../dialogs/restaurant-delivery-method-dialog/restaurant-delivery-method-dialog.component';
import { formatBusinessHours } from '../../../../shared/utils/business-hours.util';
import { FilterListComponent } from '../../../../shared/components/controllers/filter-list/filter-list.component';
import { MealCardComponent } from '../../../../shared/components/meal-card/meal-card.component';
import { MealCardSkeletonComponent } from '../../../../shared/components/loaders/meal-card-skeleton/meal-card-skeleton.component';
import { DeliveryMethodSelectedAddress } from '../../../../shared/components/shipping-method/shipping-method.component';
import { select, Store } from '@ngxs/store';
import {
  groupProductsByCategory,
  RestaurantGeneralDeliveryTypes,
  RestaurantGeneralLeadTime,
  RestaurantGeneralState,
  RestaurantState,
  WooCommerceProductCategoryObject,
  WooCommerceProductsObject
} from '../../../../shared/state/restaurant/restaurant.state';
import { CartState } from '../../../../shared/state/cart/cart.state';
import { CartItemsAction } from '../../../../shared/state/cart/cart.actions';
import { WooCommerceCartItem } from '../../../../core/interfaces/woo-commerce/api/cart/cart.interface';
import {
  GetPickupLocationsAction,
  GetRestaurantGeneralAction,
  GetRestaurantProductCategoriesAction,
  GetRestaurantProductsAction
} from '../../../../shared/state/restaurant/restaurant.actions';
import { object2Array } from '../../../../shared/utils/convert-types.utils';
import { ShippingMethod } from '../../../../core/enums/woo-commerce/shipping/shipping.enum';
import { getShippingOptions } from '../../../../shared/constants/shipping-options.constant';
import { PrimitiveTypes } from '@angular/cli/src/analytics/analytics-parameters';
import { UpsertShippingAction } from '../../../../shared/state/shipping/shipping.actions';
import {
  ShippingState,
  ShippingStateModel
} from '../../../../shared/state/shipping/shipping.state';
import {
  CategoryAvailabilityTimeSlot,
  WooCommerceProductCategory
} from '../../../../core/interfaces/woo-commerce/api/products/product-category-api.interface';
import { GoogleMapService } from '../../../../core/services/google/google-map.service';
import { convertHtml2Text } from '../../../../shared/utils/text.utils';
import {
  getDateLabel,
  getWeekdayName
} from '../../../../shared/utils/date.utils';
import {
  parseTimeRange,
  parseTimeToMinutes
} from '../../../../shared/utils/time.util';
import { WooCommerceRestaurantHolidays } from '../../../../core/interfaces/woo-commerce/api/restaurant/restaurant-api.interface';
import { getHolidayNames } from '../../../../shared/utils/holidays.utils';
import { getPickupScheduleDescription } from '../../../checkout/consts/pickup-time-options.const';
import {
  getUnavailableCartItemsForSchedule,
  isCategoryAvailableForSchedule
} from '../../../../shared/utils/category-availability.util';
import { ToastrService } from 'ngx-toastr';
import { WooCommerceDatetimeTimeSlotValue } from '../../../../core/enums/woo-commerce/api/woo-commerce-api.enum';

@UntilDestroy()
@Component({
  selector: 'app-restaurant-details-page',
  templateUrl: './restaurant-details-page.component.html',
  imports: [
    FormsModule,
    GoogleMapsModule,
    DialogModule,
    SearchComponent,
    ButtonComponent,
    OptionSwitcherComponent,
    ReactiveFormsModule,
    SkeletonDirective,
    SkeletonLoaderComponent,
    NgClass,
    FilterListComponent,
    MealCardComponent,
    MealCardSkeletonComponent,
    UpperCasePipe
  ],
  styleUrl: './restaurant-details-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RestaurantDetailsPageComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly store: Store = inject(Store);
  protected readonly ngZone: NgZone = inject(NgZone);
  protected readonly SECTION_SCROLL_OFFSET: number = 150;
  protected readonly Object = Object;
  protected readonly ButtonStyle = ButtonStyle;
  protected readonly ShippingMethod = ShippingMethod;
  protected readonly ButtonSize = ButtonSize;
  protected GOOGLE_MAP_OPTIONS: google.maps.MapOptions = {
    fullscreenControl: false,
    center: { lat: 45.50211441974471, lng: -73.5672566581298 },
    zoom: 14
  };

  protected readonly SHIPPING_OPTIONS: Signal<OptionSwitcherItem[]> = computed(
    () =>
      getShippingOptions(
        this.leadTimes().delivery_lead_time,
        this.leadTimes().pick_up_lead_time,
        this.deliveryTypes()
      )
  );

  leadTimes: Signal<RestaurantGeneralLeadTime> = select(
    RestaurantState.leadTimes
  );
  deliveryTypes: Signal<RestaurantGeneralDeliveryTypes> = select(
    RestaurantState.deliveryTypes
  );

  categorySections: Signal<any> = viewChildren('categorySection');
  protected heroVideoRef = viewChild<ElementRef<HTMLVideoElement>>('heroVideo');

  // Filters
  searchText: WritableSignal<string> = signal<string>('');
  selectedCategory: WritableSignal<number> = signal<number>(-1);

  shippingMethod: WritableSignal<ShippingMethod> = signal<ShippingMethod>(
    ShippingMethod.Pickup
  );

  // Restaurant
  restaurant: Signal<RestaurantGeneralState> = select(RestaurantState.general);
  restaurantName: Signal<string> = computed(() =>
    convertHtml2Text(this.restaurant().name)
  );
  isRestaurantOpen: Signal<boolean> = select(RestaurantState.isOpen);
  todayWorkingHours: Signal<WooCommerceRestaurantHoursValue | undefined> =
    select(RestaurantState.todayWorkingHours);
  nextOpenDay: Signal<WooCommerceRestaurantHoursValue | undefined> = select(
    RestaurantState.nextOpenDay
  );
  nextOpenDateInfo: Signal<
    { date: Date; schedule: WooCommerceRestaurantHoursValue } | undefined
  > = select(RestaurantState.nextOpenDateInfo);
  workingHours: Signal<WooCommerceRestaurantHoursValue[]> = select(
    RestaurantState.workingHours
  );
  timezone: Signal<string | undefined> = select(RestaurantState.timezone);
  holidays: Signal<WooCommerceRestaurantHolidays> = select(
    RestaurantState.holidays
  );
  isTodayHoliday: Signal<boolean> = select(RestaurantState.isTodayHoliday);
  shippingSchedule: Signal<ShippingStateModel['schedule']> = select(
    ShippingState.schedule
  );
  normalizedShippingSchedule: Signal<ShippingStateModel['schedule'] | undefined> =
    computed(() => {
      const schedule = this.shippingSchedule();
      if (!schedule) {
        return undefined;
      }

      const normalizedDate =
        schedule.date instanceof Date
          ? schedule.date
          : new Date(schedule.date as unknown as string);
      if (isNaN(normalizedDate.getTime())) {
        return undefined;
      }

      let normalizedTime: Date | string = String(schedule.time);
      if (schedule.time instanceof Date) {
        normalizedTime = schedule.time;
      } else if (typeof schedule.time === 'string') {
        const isoDateMatch = schedule.time.match(
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
        );
        if (isoDateMatch) {
          const parsedTime = new Date(schedule.time);
          if (!isNaN(parsedTime.getTime())) {
            normalizedTime = parsedTime;
          } else {
            normalizedTime = schedule.time;
          }
        } else {
          normalizedTime = schedule.time;
        }
      }

      return {
        ...schedule,
        date: normalizedDate,
        time: normalizedTime
      };
    });
  activeShippingSchedule: Signal<ShippingStateModel['schedule'] | undefined> =
    computed(() => {
      const schedule = this.normalizedShippingSchedule();
      if (!schedule) {
        return undefined;
      }

      return this.isScheduleExpired(schedule, this.timezone())
        ? undefined
        : schedule;
    });
  cartItems: Signal<WooCommerceCartItem[]> = select(CartState.items);
  selectedScheduleDescription: Signal<string | undefined> = computed(() =>
    getPickupScheduleDescription(this.activeShippingSchedule())
  );

  // Computed signal to get today's holiday reason (if any)
  todayHolidayReason: Signal<string | undefined> = computed(() => {
    const holidays = this.holidays();
    const tz = this.timezone();
    const now = this.currentTime();

    const reasons = getHolidayNames(now, holidays, tz);
    if (reasons && reasons.length > 0) {
      // Capitalize first letter of each reason
      return reasons
        .map((r) => r.charAt(0).toUpperCase() + r.slice(1))
        .join(', ');
    }
    return undefined;
  });

  // Countdown timer for closing soon warning
  currentTime: WritableSignal<Date> = signal(new Date());

  minutesUntilClosing: Signal<number | null> = computed(() => {
    const isOpen = this.isRestaurantOpen();
    const todayHours = this.todayWorkingHours();
    const tz = this.timezone();
    const now = this.currentTime();

    if (!isOpen || !todayHours || !todayHours.close_time) {
      return null;
    }

    // Parse close time (e.g., "6:00 pm" or "11:30 pm")
    const closeTimeMinutes = parseTimeToMinutes(todayHours.close_time);

    // Get current time in minutes since midnight (using local time, as working hours are likely in local time)
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // Calculate difference
    const minutesLeft = closeTimeMinutes - currentMinutes;

    return minutesLeft > 0 ? minutesLeft : null;
  });

  showClosingSoonWarning: Signal<boolean> = computed(() => {
    const minutes = this.minutesUntilClosing();
    return minutes != null && minutes <= 59 && minutes > 0;
  });

  nextOpeningInfo: Signal<{ day: string; time: string } | undefined> = computed(
    () => {
      const nextOpenInfo = this.nextOpenDateInfo();
      if (!nextOpenInfo) {
        return undefined;
      }

      const tz = this.timezone();
      const now = this.currentTime();
      const dayLabel = getDateLabel(nextOpenInfo.date, now, tz);
      return { day: dayLabel, time: nextOpenInfo.schedule.start_time || '' };
    }
  );

  willOpenLaterToday: Signal<boolean> = computed(() => {
    // If today is a holiday, the restaurant won't open later today
    if (this.isTodayHoliday()) {
      return false;
    }

    const today = this.todayWorkingHours();
    if (!today || !today.is_opened || !today.start_time) {
      return false;
    }

    if (this.isRestaurantOpen()) {
      return false;
    }

    const now = new Date();
    const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
    const openTimeInMinutes = parseTimeToMinutes(today.start_time);

    return currentTimeInMinutes < openTimeInMinutes;
  });
  banners: Signal<WooCommerceRestaurantBanner[]> = select(
    RestaurantState.banners
  );

  // Products
  categories: Signal<WooCommerceProductCategoryObject> = select(
    RestaurantState.productCategories
  );

  categoriesOrder: Signal<number[]> = select(
    RestaurantState.productCategoriesOrder
  );

  availableCategories: Signal<WooCommerceProductCategoryObject> = computed(
    () => {
      const categories = this.categories();
      const selectedSchedule = this.activeShippingSchedule();
      const timezone = this.timezone();

      const available: WooCommerceProductCategoryObject = {};
      Object.values(categories).forEach((category) => {
        if (selectedSchedule) {
          if (
            isCategoryAvailableForSchedule(
              category,
              selectedSchedule.date,
              selectedSchedule.time as Date | string,
              timezone
            )
          ) {
            available[category.id] = category;
          }
          return;
        }

        const now = this.currentTime();
        if (this.isCategoryAvailable(category, now, timezone)) {
          available[category.id] = category;
        }
      });

      return available;
    }
  );

  sortedAvailableCategories: Signal<WooCommerceProductCategory[]> = computed(
    () => {
      const order = this.categoriesOrder();
      const available = this.availableCategories();
      return order.filter((id) => available[id]).map((id) => available[id]);
    }
  );

  sortedAllCategories: Signal<WooCommerceProductCategory[]> = computed(() => {
    const order = this.categoriesOrder();
    const categories = this.categories();
    const available = this.availableCategories();

    const availableList = order
      .filter((id) => categories[id] && available[id])
      .map((id) => categories[id]);
    const unavailableList = order
      .filter((id) => categories[id] && !available[id])
      .map((id) => categories[id]);

    return [...availableList, ...unavailableList];
  });

  products: Signal<WooCommerceProductsObject> = select(
    RestaurantState.products
  );

  filteredProducts: Signal<WooCommerceProductsObject> = computed(() => {
    const rawSearchText: string = this.searchText();
    const products: WooCommerceProductsObject = this.products();

    const allProducts: WooCommerceProduct[] = object2Array(products || {});

    const filteredArray: WooCommerceProduct[] = allProducts.filter(
      (product: WooCommerceProduct): boolean => {
        const productName: string = product?.name?.toLowerCase();
        const searchText: string = rawSearchText.toLowerCase();
        return productName.includes(searchText);
      }
    );

    return groupProductsByCategory(filteredArray);
  });

  sortedFilteredProducts: Signal<
    { key: string; value: WooCommerceProduct[] }[]
  > = computed(() => {
    const filtered = this.filteredProducts();
    const allCategories = this.sortedAllCategories();

    return allCategories
      .filter((cat) => filtered[cat.id])
      .map((cat) => ({ key: cat.id.toString(), value: filtered[cat.id] }));
  });

  // Loaders
  /**
   * Only true while the `/store/info` HTTP request is in flight AND we have
   * nothing to show yet. When `RestaurantState.ngxsOnInit` (or
   * `ClearRestaurantAction` after a location switch) has already seeded the
   * restaurant slice from the `localStorage` `/store/info` cache, we render
   * the cached logo/name/hours immediately and skip the skeleton while the
   * background refresh runs.
   */
  isRestaurantLoading: Signal<boolean> = computed(() => {
    if (!this.isRestaurantGeneralFetching()) {
      return false;
    }
    return !this.hasSeededRestaurantData();
  });
  private readonly isRestaurantGeneralFetching: Signal<boolean> = select(
    RestaurantState.isLoading('general')
  );
  private readonly hasSeededRestaurantData: Signal<boolean> = computed(() => {
    const general = this.restaurant();
    return !!(general?.name || general?.logo || general?.hero);
  });
  isLoadingCategories: Signal<boolean> = select(
    RestaurantState.isLoading('categories')
  );
  isLoadingProducts: Signal<boolean> = select(
    RestaurantState.isLoading('products')
  );
  isMarketLoading: WritableSignal<boolean> = signal(false);

  private isProgrammaticScroll = false;
  private scrollTimeout: any;

  constructor(
    private dialog: Dialog,
    private googleMapService: GoogleMapService,
    private toastrService: ToastrService
  ) {
    effect(
      () => {
        this.timezone();
        this.shippingSchedule();
        this.currentTime();
        this.checkAndClearPastSchedule();
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      const heroUrl = this.restaurant().hero || '';
      const videoElementRef = this.heroVideoRef();

      if (!videoElementRef || !this.isVideoUrl(heroUrl)) {
        return;
      }

      const videoElement = videoElementRef.nativeElement;
      videoElement.muted = true;
      videoElement.loop = true;
      videoElement.autoplay = true;
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('preload', 'auto');

      const playPromise = videoElement.play();
      if (playPromise instanceof Promise) {
        playPromise.catch(() => {
          videoElement.muted = true;
          videoElement.play().catch(() => undefined);
        });
      }
    });
  }

  ngOnInit() {
    this.initShippingMethod();

    this.getRestaurantDetails();
    this.initMarket();

    this.subscribeOnCartChanges();

    // Update current time every minute for countdown timer
    interval(60000)
      .pipe(startWith(0), untilDestroyed(this))
      .subscribe(() => {
        this.currentTime.set(new Date());
      });
  }

  private categoryPositions: { id: number; top: number; bottom: number }[] = [];
  private resizeObserver: ResizeObserver | null = null;

  ngAfterViewInit() {
    this.handleScrollEvent();
    this.initResizeObserver();

    // Re-calculate positions when categories change
    effect(() => {
      this.categorySections();
      // Use setTimeout to allow layout to settle
      setTimeout(() => this.calculateCategoryPositions(), 100);
    });
  }

  onShippingMethodChange(event: PrimitiveTypes) {
    const shippingMethod: ShippingMethod = event as ShippingMethod;
    // Reset methodDetails and schedule when switching between delivery/pickup
    this.store.dispatch(
      new UpsertShippingAction({
        method: shippingMethod,
        methodDetails: undefined,
        schedule: undefined
      })
    );
  }

  onMapInitialized(
    map: google.maps.Map,
    address: string = this.restaurant().address
  ): void {
    this.googleMapService.geocodeAddress(address).subscribe({
      next: (address) => {
        new google.maps.Marker({
          title: 'Restaurant Position',
          position: address.center,
          map: map
        });

        this.GOOGLE_MAP_OPTIONS = {
          ...this.GOOGLE_MAP_OPTIONS,
          center: address.center
        };
      }
    });
  }

  openScheduleModal() {
    const businessHours = this.workingHours() || [];

    const dialogRef = this.dialog.open(ScheduleOrderDialogComponent, {
      data: {
        title: 'Sorry we are not open at the moment',
        workingHours: formatBusinessHours(businessHours),
        rawWorkingHours: businessHours
      },
      width: '100%',
      maxWidth: '520px'
    });

    dialogRef.closed.pipe(filter((address) => address != null)).subscribe({
      next: (address: any) => {
        const nextShippingState = address as ShippingStateModel;
        const nextSchedule = nextShippingState.schedule;
        if (!nextSchedule || this.cartItems().length === 0) {
          this.store.dispatch(new UpsertShippingAction(address));
          return;
        }

        const timezone = this.timezone();
        const unavailableItems = this.getUnavailableCartItemsForSchedule(
          nextSchedule,
          timezone
        );
        if (unavailableItems.length === 0) {
          this.store.dispatch(new UpsertShippingAction(address));
          return;
        }

        const confirmDialogRef: DialogRef<boolean, ConfirmDialogComponent> =
          this.dialog.open(ConfirmDialogComponent, {
            data: {
              title: 'Update scheduled order?',
              message:
                'Some items in your cart are not available at the new scheduled time. If you continue, those items will be removed from your cart.',
              cancelLabel: 'Cancel',
              confirmLabel: 'Reschedule'
            }
          });

        confirmDialogRef.closed.pipe(filter((v) => v != null)).subscribe({
          next: (confirmed) => {
            if (!confirmed) {
              return;
            }

            const removeActions = unavailableItems.map(
              (item) => new CartItemsAction.Remove(item.key)
            );
            this.store.dispatch([
              ...removeActions,
              new UpsertShippingAction(address)
            ]);
          }
        });
      }
    });
  }

  checkDeliveryMethodModal() {
    if (this.store.selectSnapshot(ShippingState.shippingState).method != null) {
      console.log('Delivery Method is already set!');
      return;
    }

    const dialogRef: DialogRef<
      DeliveryMethodSelectedAddress,
      RestaurantDeliveryMethodDialogComponent
    > = this.dialog.open(RestaurantDeliveryMethodDialogComponent, {
      data: { title: 'How would you like to get this order?' },
      width: '100%',
      maxWidth: '500px'
    });

    dialogRef.closed.pipe(filter((shipping) => shipping != null)).subscribe({
      next: (deliveryMethod: DeliveryMethodSelectedAddress) => {
        this.store.dispatch(new UpsertShippingAction(deliveryMethod));
      }
    });
  }

  openRestaurantDetailsModal(): void {
    this.dialog.open(RestaurantDetailsDialogComponent, {
      data: {
        restaurant: this.restaurant(),
        workingHours: this.workingHours()
      },
      width: '100%',
      maxWidth: '800px'
    });
  }

  openMealSelectionModal(data: WooCommerceProduct): void {
    this.dialog.open(MealSelectionDialogComponent, {
      width: '100%',
      maxWidth: '800px',
      panelClass: 'responsive-modal',
      data
    });
  }

  onCategoryChange(categoryId: number) {
    const element: HTMLElement | null = document.querySelector(
      `[data-category="${categoryId}"]`
    );

    if (element) {
      this.isProgrammaticScroll = true;
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);

      // Add a small extra offset to ensure we land definitely inside the section
      // and not exactly on the border where floating point math might fail
      const yOffset: number = -this.SECTION_SCROLL_OFFSET + 2;

      window.scrollTo({
        top: element.getBoundingClientRect()?.top + window?.scrollY + yOffset,
        behavior: 'smooth'
      });

      this.scrollTimeout = setTimeout(() => {
        this.isProgrammaticScroll = false;
      }, 1000);
    }
  }

  private calculateCategoryPositions() {
    const scrollY = window.scrollY;
    this.categoryPositions = this.categorySections().map((section: any) => {
      const el = section.nativeElement as HTMLElement;
      const rect = el.getBoundingClientRect();
      return {
        id: parseInt(el.getAttribute('data-category')!, 10),
        top: rect.top + scrollY,
        bottom: rect.bottom + scrollY
      };
    });
  }

  private initResizeObserver() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.ngZone.runOutsideAngular(() => {
          this.calculateCategoryPositions();
        });
      });
      this.resizeObserver.observe(document.body);
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private getCurrentVisibleCategory(): number | null {
    if (this.categoryPositions.length === 0) {
      this.calculateCategoryPositions();
    }

    const offset: number = this.SECTION_SCROLL_OFFSET;
    const scrollY = window.scrollY;
    const targetY = scrollY + offset;

    const activeSection = this.categoryPositions.find(
      (pos) => pos.top <= targetY && pos.bottom > targetY
    );

    return activeSection ? activeSection.id : null;
  }

  private handleScrollEvent() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'scroll', { passive: true })
        .pipe(
          auditTime(50),
          map(() => this.getCurrentVisibleCategory()),
          distinctUntilChanged(),
          untilDestroyed(this)
        )
        .subscribe((currentCategoryKey: number | null) => {
          if (!currentCategoryKey || this.isProgrammaticScroll) {
            return;
          }

          this.ngZone.run(() => {
            this.selectedCategory.set(currentCategoryKey);
          });
        });
    });
  }

  private initShippingMethod() {
    const shippingState: ShippingStateModel = this.store.selectSnapshot(
      ShippingState.shippingState
    );

    this.shippingMethod.set(shippingState.method || ShippingMethod.Delivery);
  }

  // Market
  private initMarket() {
    this.isMarketLoading.set(true);

    forkJoin([this.getProductsCategories(), this.getProducts()])
      .pipe(
        untilDestroyed(this),
        finalize(() => this.isMarketLoading.set(false))
      )
      .subscribe({
        next: () => {
          console.log('Market has been initialized!');
        }
      });
  }

  private getProductsCategories() {
    return this.store.dispatch(new GetRestaurantProductCategoriesAction());
  }

  private getProducts() {
    return this.store.dispatch(new GetRestaurantProductsAction());
  }

  // Restaurant
  private getRestaurantDetails() {
    this.store.dispatch([
      new GetRestaurantGeneralAction(),
      new GetPickupLocationsAction()
    ]);
  }

  /**
   * If the persisted schedule is in the past (e.g. after reload or time passing), clear it
   * so the menu and banner match instant ordering — same rules as the schedule dialog.
   */
  private checkAndClearPastSchedule(): void {
    const schedule = this.normalizedShippingSchedule();
    if (!schedule) {
      return;
    }

    if (!this.isScheduleExpired(schedule, this.timezone())) {
      return;
    }

    this.store.dispatch(new UpsertShippingAction({ schedule: undefined }));
    this.toastrService.warning(
      'The selected date or time has passed. Please choose a new schedule or order now.',
      'Schedule expired'
    );
  }

  private isScheduleExpired(
    schedule: NonNullable<ShippingStateModel['schedule']>,
    timezone?: string
  ): boolean {
    const scheduleDate =
      schedule.date instanceof Date
        ? schedule.date
        : new Date(schedule.date as unknown as string);
    if (isNaN(scheduleDate.getTime())) {
      return false;
    }

    const scheduleTime =
      schedule.time instanceof Date ? schedule.time : String(schedule.time);
    const nowInTz = this.getNowInTimezone(timezone);

    if (this.isDateInPastWithTimezone(scheduleDate, nowInTz)) {
      return true;
    }

    return (
      this.isSameDayWithTimezone(scheduleDate, nowInTz) &&
      this.isTimeInPastWithTimezone(scheduleTime, nowInTz)
    );
  }

  private getNowInTimezone(timezone?: string): {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
    totalMinutes: number;
  } {
    const now = new Date();

    if (timezone) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false
        });
        const parts = formatter.formatToParts(now);
        const year = parseInt(
          parts.find((part) => part.type === 'year')?.value || '0',
          10
        );
        const month =
          parseInt(parts.find((part) => part.type === 'month')?.value || '1', 10) -
          1;
        const day = parseInt(
          parts.find((part) => part.type === 'day')?.value || '1',
          10
        );
        const hours = parseInt(
          parts.find((part) => part.type === 'hour')?.value || '0',
          10
        );
        const minutes = parseInt(
          parts.find((part) => part.type === 'minute')?.value || '0',
          10
        );

        return {
          year,
          month,
          day,
          hours,
          minutes,
          totalMinutes: hours * 60 + minutes
        };
      } catch (error) {
        console.error('Failed to parse restaurant timezone', error);
      }
    }

    const hours = now.getHours();
    const minutes = now.getMinutes();

    return {
      year: now.getFullYear(),
      month: now.getMonth(),
      day: now.getDate(),
      hours,
      minutes,
      totalMinutes: hours * 60 + minutes
    };
  }

  private isDateInPastWithTimezone(
    date: Date,
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): boolean {
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    const dateDay = date.getDate();

    if (dateYear < nowInTz.year) return true;
    if (dateYear > nowInTz.year) return false;
    if (dateMonth < nowInTz.month) return true;
    if (dateMonth > nowInTz.month) return false;
    return dateDay < nowInTz.day;
  }

  private isSameDayWithTimezone(
    date: Date,
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): boolean {
    return (
      date.getFullYear() === nowInTz.year &&
      date.getMonth() === nowInTz.month &&
      date.getDate() === nowInTz.day
    );
  }

  private isTimeInPastWithTimezone(
    time: Date | string,
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): boolean {
    if (time instanceof Date) {
      const timeMinutes = time.getHours() * 60 + time.getMinutes();
      return timeMinutes <= nowInTz.totalMinutes;
    }

    if (time.toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap) {
      return false;
    }

    const timeRange = parseTimeRange(time);
    if (timeRange) {
      const endMinutes = timeRange.end.milliseconds / (60 * 1000);
      return endMinutes <= nowInTz.totalMinutes;
    }

    return false;
  }

  private subscribeOnCartChanges() {
    this.store
      .select(CartState.items)
      .pipe(
        pairwise(),
        filter(
          ([prev, curr]: [WooCommerceCartItem[], WooCommerceCartItem[]]) =>
            prev.length === 0 && curr.length > 0
        ),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.checkDeliveryMethodModal();
      });
  }

  private getUnavailableCartItemsForSchedule(
    schedule: NonNullable<ShippingStateModel['schedule']>,
    timezone?: string
  ) {
    const scheduleDate =
      schedule.date instanceof Date
        ? schedule.date
        : new Date(schedule.date as unknown as string);
    const scheduleTime =
      schedule.time instanceof Date ? schedule.time : String(schedule.time);

    return getUnavailableCartItemsForSchedule(
      this.cartItems(),
      this.products(),
      this.categories(),
      scheduleDate,
      scheduleTime,
      timezone
    );
  }

  protected isVideoUrl(url: string): boolean {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const lowercaseUrl = url.toLowerCase();
    return videoExtensions.some((ext) => lowercaseUrl.endsWith(ext));
  }

  protected isCategoryUnavailable(categoryId: number): boolean {
    return !this.availableCategories()[categoryId];
  }

  protected getCategoryAvailabilityLabel(
    category: WooCommerceProductCategory
  ): string | null {
    if (!category.hmp_category_availability) {
      return null;
    }

    const availability = category.hmp_category_availability.weekdays;
    const weekdayKeys = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday'
    ];
    const shortNames: Record<string, string> = {
      monday: 'Mon',
      tuesday: 'Tue',
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat',
      sunday: 'Sun'
    };

    const enabledDays = weekdayKeys.filter((day) => {
      const config = availability[day];
      return config && Number(config.enabled);
    });

    if (enabledDays.length === 0) {
      return 'Currently unavailable';
    }

    const formatTime = (hours: number, mins: number): string => {
      const suffix = hours >= 12 ? 'PM' : 'AM';
      const h = hours % 12 || 12;
      const m = mins.toString().padStart(2, '0');
      return `${h}:${m} ${suffix}`;
    };

    const isAllDay = (config: CategoryAvailabilityTimeSlot): boolean =>
      Number(config.from_hours) === 0 &&
      Number(config.from_mins) === 0 &&
      Number(config.to_hours) === 23 &&
      Number(config.to_mins) >= 55;

    const coversRestaurantHours = (
      config: CategoryAvailabilityTimeSlot
    ): boolean => {
      const todayHours = this.todayWorkingHours();
      if (!todayHours?.start_time || !todayHours?.close_time) return false;
      const restaurantStart = parseTimeToMinutes(todayHours.start_time);
      const restaurantEnd = parseTimeToMinutes(todayHours.close_time);
      const catStart =
        Number(config.from_hours) * 60 + Number(config.from_mins);
      const catEnd = Number(config.to_hours) * 60 + Number(config.to_mins);
      return catStart <= restaurantStart && catEnd >= restaurantEnd;
    };

    const firstConfig = availability[enabledDays[0]];

    if (
      enabledDays.length === 7 &&
      (isAllDay(firstConfig) || coversRestaurantHours(firstConfig))
    ) {
      return null;
    }

    const buildDayRanges = (days: string[]): string => {
      const indices = days.map((d) => weekdayKeys.indexOf(d));
      const ranges: string[] = [];
      let rangeStart = indices[0];
      let rangeEnd = indices[0];

      for (let i = 1; i < indices.length; i++) {
        if (indices[i] === rangeEnd + 1) {
          rangeEnd = indices[i];
        } else {
          ranges.push(
            rangeStart === rangeEnd
              ? shortNames[weekdayKeys[rangeStart]]
              : `${shortNames[weekdayKeys[rangeStart]]} – ${shortNames[weekdayKeys[rangeEnd]]}`
          );
          rangeStart = indices[i];
          rangeEnd = indices[i];
        }
      }
      ranges.push(
        rangeStart === rangeEnd
          ? shortNames[weekdayKeys[rangeStart]]
          : `${shortNames[weekdayKeys[rangeStart]]} – ${shortNames[weekdayKeys[rangeEnd]]}`
      );

      return ranges.join(', ');
    };

    const timeLabel =
      isAllDay(firstConfig) || coversRestaurantHours(firstConfig)
        ? ''
        : ` from ${formatTime(firstConfig.from_hours, firstConfig.from_mins)} until ${formatTime(firstConfig.to_hours, firstConfig.to_mins)}`;

    if (enabledDays.length === 7) {
      return `Available${timeLabel}`;
    }

    const dayRanges = buildDayRanges(enabledDays);
    return `Available ${dayRanges}${timeLabel}`;
  }

  private isCategoryAvailable(
    category: WooCommerceProductCategory,
    now: Date = new Date(),
    timezone?: string
  ): boolean {
    if (!category.hmp_category_availability) {
      return true;
    }

    const weekdayName = getWeekdayName(now, timezone).toLowerCase();
    const availability = category.hmp_category_availability.weekdays;
    // @ts-ignore
    const dayConfig = availability[weekdayName];

    if (!dayConfig || !dayConfig.enabled) {
      return false;
    }

    let currentMinutes: number;
    if (timezone) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
      });
      const parts = formatter.formatToParts(now);
      const hours = parseInt(
        parts.find((p) => p.type === 'hour')?.value || '0',
        10
      );
      const minutes = parseInt(
        parts.find((p) => p.type === 'minute')?.value || '0',
        10
      );
      currentMinutes = hours * 60 + minutes;
    } else {
      currentMinutes = now.getHours() * 60 + now.getMinutes();
    }

    const startMinutes =
      Number(dayConfig.from_hours) * 60 + Number(dayConfig.from_mins);
    const endMinutes =
      Number(dayConfig.to_hours) * 60 + Number(dayConfig.to_mins);

    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }
}
